import * as THREE from 'three';
const createEnhancedFlooring = (scene) => {
  // Professional epoxy resin floor base with subtle texture
  const baseGeometry = new THREE.PlaneGeometry(20, 15);
  const baseMaterial = new THREE.MeshLambertMaterial({ 
    color: 0xf5f5f5, // Light grey epoxy base
    transparent: false
  });
  const baseFloor = new THREE.Mesh(baseGeometry, baseMaterial);
  baseFloor.rotation.x = -Math.PI / 2;
  baseFloor.position.y = 0;
  baseFloor.receiveShadow = true;
  scene.add(baseFloor);

  // Create premium ceramic tiles with realistic variations
  const tileSize = 0.6; // Smaller, more realistic tile size
  const tilesX = Math.floor(20 / tileSize);
  const tilesZ = Math.floor(15 / tileSize);
  
  for (let x = 0; x < tilesX; x++) {
    for (let z = 0; z < tilesZ; z++) {
      // Create individual tiles with subtle height variations
      const tileGeometry = new THREE.BoxGeometry(tileSize * 0.98, 0.02, tileSize * 0.98);
      
      // Premium white/light grey ceramic with natural variation
      const brightness = 0.92 + Math.random() * 0.08;
      const warmth = 0.95 + Math.random() * 0.03; // Slight warm tint
      const tileColor = new THREE.Color(brightness, brightness * warmth, brightness * 0.98);
      
      const tileMaterial = new THREE.MeshLambertMaterial({ 
        color: tileColor
      });
      
      const tile = new THREE.Mesh(tileGeometry, tileMaterial);
      tile.position.set(
        (x - tilesX/2) * tileSize + tileSize/2,
        0.01 + Math.random() * 0.002, // Slight height variation
        (z - tilesZ/2) * tileSize + tileSize/2
      );
      tile.castShadow = true;
      tile.receiveShadow = true;
      scene.add(tile);
    }
  }

  // Professional grout lines - thin and realistic
  const groutWidth = 0.03;
  const groutMaterial = new THREE.MeshLambertMaterial({ 
    color: 0xc0c0c0 // Light grey grout
  });

  // Horizontal grout lines
  for (let x = 0; x <= tilesX; x++) {
    const groutGeometry = new THREE.BoxGeometry(groutWidth, 0.005, 15);
    const grout = new THREE.Mesh(groutGeometry, groutMaterial);
    grout.position.set(
      (x - tilesX/2) * tileSize,
      0.005,
      0
    );
    scene.add(grout);
  }

  // Vertical grout lines
  for (let z = 0; z <= tilesZ; z++) {
    const groutGeometry = new THREE.BoxGeometry(20, 0.005, groutWidth);
    const grout = new THREE.Mesh(groutGeometry, groutMaterial);
    grout.position.set(
      0,
      0.005,
      (z - tilesZ/2) * tileSize
    );
    scene.add(grout);
  }

  // Professional drainage system - recessed channels
  const drainDepth = 0.08;
  const drainWidth = 0.15;
  
  // Create recessed drainage channels
  const drainMaterial = new THREE.MeshLambertMaterial({ 
    color: 0x2a2a2a // Dark drainage material
  });

  // Perimeter drainage - left side
  const leftDrainGeometry = new THREE.BoxGeometry(drainWidth, drainDepth, 14);
  const leftDrain = new THREE.Mesh(leftDrainGeometry, drainMaterial);
  leftDrain.position.set(-9.5, -drainDepth/2 + 0.01, 0);
  scene.add(leftDrain);

  // Right side
  const rightDrain = new THREE.Mesh(leftDrainGeometry, drainMaterial);
  rightDrain.position.set(9.5, -drainDepth/2 + 0.01, 0);
  scene.add(rightDrain);

  // Front and back drainage
  const frontBackDrainGeometry = new THREE.BoxGeometry(19, drainDepth, drainWidth);
  const frontDrain = new THREE.Mesh(frontBackDrainGeometry, drainMaterial);
  frontDrain.position.set(0, -drainDepth/2 + 0.01, -7);
  scene.add(frontDrain);

  const backDrain = new THREE.Mesh(frontBackDrainGeometry, drainMaterial);
  backDrain.position.set(0, -drainDepth/2 + 0.01, 7);
  scene.add(backDrain);

  // Add drainage grates with realistic detail
  const grateSpacing = 2;
  const grateMaterial = new THREE.MeshLambertMaterial({ color: 0x404040 });
  
  // Left side grates
  for (let i = -6; i <= 6; i += grateSpacing) {
    const grateGeometry = new THREE.BoxGeometry(0.12, 0.02, 0.3);
    const grate = new THREE.Mesh(grateGeometry, grateMaterial);
    grate.position.set(-9.5, 0.02, i);
    scene.add(grate);
    
    // Grate slats
    for (let j = 0; j < 5; j++) {
      const slatGeometry = new THREE.BoxGeometry(0.1, 0.01, 0.02);
      const slat = new THREE.Mesh(slatGeometry, grateMaterial);
      slat.position.set(-9.5, 0.025, i - 0.12 + j * 0.06);
      scene.add(slat);
    }
  }

  // Right side grates (mirror)
  for (let i = -6; i <= 6; i += grateSpacing) {
    const grateGeometry = new THREE.BoxGeometry(0.12, 0.02, 0.3);
    const grate = new THREE.Mesh(grateGeometry, grateMaterial);
    grate.position.set(9.5, 0.02, i);
    scene.add(grate);
    
    for (let j = 0; j < 5; j++) {
      const slatGeometry = new THREE.BoxGeometry(0.1, 0.01, 0.02);
      const slat = new THREE.Mesh(slatGeometry, grateMaterial);
      slat.position.set(9.5, 0.025, i - 0.12 + j * 0.06);
      scene.add(slat);
    }
  }

  // Central floor drainage - subtle cross pattern
  const centralDrainGeometry = new THREE.BoxGeometry(0.08, 0.03, 12);
  const centralDrainX = new THREE.Mesh(centralDrainGeometry, drainMaterial);
  centralDrainX.position.set(0, -0.01, 0);
  scene.add(centralDrainX);

  const centralDrainZ = new THREE.Mesh(new THREE.BoxGeometry(16, 0.03, 0.08), drainMaterial);
  centralDrainZ.position.set(0, -0.01, 0);
  scene.add(centralDrainZ);

  // Add realistic wear patterns and subtle staining
  for (let i = 0; i < 12; i++) {
    const wearGeometry = new THREE.CircleGeometry(0.1 + Math.random() * 0.3, 8);
    const wearIntensity = 0.88 + Math.random() * 0.08;
    const wearMaterial = new THREE.MeshLambertMaterial({ 
      color: new THREE.Color(wearIntensity, wearIntensity * 0.98, wearIntensity * 0.96),
      transparent: true,
      opacity: 0.7
    });
    const wear = new THREE.Mesh(wearGeometry, wearMaterial);
    wear.rotation.x = -Math.PI / 2;
    wear.position.set(
      (Math.random() - 0.5) * 16,
      0.015,
      (Math.random() - 0.5) * 12
    );
    scene.add(wear);
  }

  // Add subtle water marks near drainage areas
  for (let i = 0; i < 6; i++) {
    const watermarkGeometry = new THREE.RingGeometry(0.2, 0.4, 8);
    const watermarkMaterial = new THREE.MeshLambertMaterial({ 
      color: 0xd0d0d0,
      transparent: true,
      opacity: 0.4
    });
    const watermark = new THREE.Mesh(watermarkGeometry, watermarkMaterial);
    watermark.rotation.x = -Math.PI / 2;
    
    // Position near drainage areas
    const side = Math.random() > 0.5 ? 1 : -1;
    watermark.position.set(
      side * (8 + Math.random() * 2),
      0.016,
      (Math.random() - 0.5) * 10
    );
    scene.add(watermark);
  }

  // Add subtle reflective coating effect
  const coatingGeometry = new THREE.PlaneGeometry(19.5, 14.5);
  const coatingMaterial = new THREE.MeshLambertMaterial({ 
    color: 0xffffff,
    transparent: true,
    opacity: 0.1
  });
  const coating = new THREE.Mesh(coatingGeometry, coatingMaterial);
  coating.rotation.x = -Math.PI / 2;
  coating.position.y = 0.025;
  scene.add(coating);
};


export default createEnhancedFlooring;
